import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatMenuTrigger } from "@angular/material/menu";
import { WindowService } from "@merri/common";
import { Subject, merge } from "rxjs";
import { takeUntil } from "rxjs/operators";
import {
  AppColors,
  HELP_EMAIL,
  LoggingService,
  PartnerUserService,
  RedirectService,
  SessionService,
  SessionUser,
  USER_TYPE_CLIENT,
} from "src/app/core";
import { EventsService } from "src/app/features/events";
import { HeaderService } from "src/app/layout/services/header.service";
import { LayoutService } from "src/app/layout/services/layout.service";

const Workflow = "Protected";
const WorkflowSource = "ProtectedHeader";

@Component({
  selector: "app-protected-header",
  templateUrl: "./protected-header.component.html",
  styleUrls: ["./protected-header.component.scss"],
  standalone: false,
})
export class ProtectedHeaderComponent implements OnInit, OnDestroy {
  private _destroyed$: Subject<void> = new Subject();

  @Input() user: SessionUser;

  @ViewChild("menuTrigger", { static: true }) menuTrigger: MatMenuTrigger;

  mobileView: boolean;
  isHeaderMobile: boolean;
  guestMode: boolean;
  eventNavigationVisible: boolean;
  showReferralBtn: boolean;
  showEventSelector: boolean = false;

  readonly HELP_EMAIL = HELP_EMAIL;

  constructor(
    private _windowService: WindowService,
    private _layoutService: LayoutService,
    private _eventsService: EventsService,
    private _redirectService: RedirectService,
    private _sessionService: SessionService,
    private _partnerUserService: PartnerUserService,
    private _hostEl: ElementRef<HTMLElement>,
    private _loggingService: LoggingService,
    private _headerService: HeaderService,
  ) {}

  ngOnInit() {
    merge(this._windowService.observeMax$(850), this._windowService.observeMax$(579))
      .pipe(takeUntil(this._destroyed$))
      .subscribe({
        next: () => {
          this.mobileView = this._windowService.matchesMax(850);
          this.isHeaderMobile = this._windowService.matchesMax(579);
        },
      });

    this._layoutService.layoutChange$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: data => {
        this.eventNavigationVisible = data.eventNavigationVisible;
        this._hostEl.nativeElement.style.setProperty("--buttonColor", AppColors.Accent);
      },
    });

    this._sessionService.sessionUser$.pipe(takeUntil(this._destroyed$)).subscribe({
      next: () => {
        this.guestMode = this._sessionService.isGuestUser(this.user.userType);
        this.showReferralBtn =
          this._sessionService.isPartnerUser() && this._partnerUserService.getCurrentPartnerUser()?.canReceiveReferralRewards;

        this.showEventSelector = this.user?.userType === USER_TYPE_CLIENT;
      },
    });
  }

  ngOnDestroy(): void {
    this._headerService.setHeaderDrawer(null);
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  showDisplayName(): boolean {
    return !this.eventNavigationVisible || this.user.userType !== USER_TYPE_CLIENT;
  }

  getDisplayName(): string {
    if (this.user.userType === USER_TYPE_CLIENT) {
      const event = this._eventsService.getCurrentEvent();

      if (event) {
        return event.summary.name;
      }
    }

    if (this.guestMode) {
      return this.user.displayName;
    }

    return "Dashboard";
  }

  onTutorialsClick(): void {
    this._loggingService.logWorkflow(
      "Help Center click",
      "ProtectedHeaderComponent.onTutorialsClick",
      Workflow,
      "HelpCenterClick",
      WorkflowSource,
    );
    this._redirectService.goToPartnerPortalHelpGuide();
  }

  onPortalLinkClick(): void {
    this._loggingService.logWorkflow(
      "Portal Link click",
      "ProtectedHeaderComponent.onPortalLinkClick",
      Workflow,
      "PortalLinkClick",
      WorkflowSource,
    );
  }
}
